/**
 * Device operations for CDP SDK.
 */

import { CDPSession, getSession } from '../session';

export class DeviceManager {
  constructor(private readonly session: CDPSession = getSession()) {}

  async listDevices(
    projectUuid: string,
    page = 1,
    size = 20,
    name?: string,
    deviceTypeUuid?: string,
  ): Promise<Record<string, any>> {
    const options: Record<string, string> = { projectUuid };
    if (name) options.name = name;
    if (deviceTypeUuid) options.deviceTypeUuid = deviceTypeUuid;
    const params: Record<string, any> = { current: page, size, options: JSON.stringify(options) };
    return this.session.get('/api/cdp/cdp_device/page', { params });
  }

  async getDevice(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/cdp_device/get/${uuid}`);
  }

  async createDevice(
    projectUuid: string,
    name: string,
    code: string,
    deviceTypeUuid: string,
    config?: Record<string, any>,
  ): Promise<Record<string, any>> {
    const data = {
      data: {
        projectUuid,
        name,
        code,
        deviceTypeUuid,
        config: config ?? {},
      },
    };
    return this.session.post('/api/cdp/cdp_device/create', { data });
  }

  async updateDevice(
    uuid: string,
    name?: string,
    config?: Record<string, any>,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { data: { uuid } };
    if (name) data.data.name = name;
    if (config) data.data.config = config;
    return this.session.post('/api/cdp/cdp_device/update', { data });
  }

  async deleteDevice(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/cdp_device/delete/${uuid}`);
  }

  async batchDeleteDevices(uuids: string[]): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_device/batchDelete', { data: uuids });
  }

  async listDeviceTypes(page = 1, size = 20, name?: string): Promise<Record<string, any>> {
    const options: Record<string, string> = {};
    if (name) options.name = name;
    const params: Record<string, any> = { current: page, size };
    if (Object.keys(options).length) params.options = JSON.stringify(options);
    return this.session.get('/api/cdp/cdp_devicetype/extend/page2', { params });
  }

  async getDeviceType(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/cdp_devicetype/extend/get/${uuid}`);
  }

  async createDeviceType(
    name: string,
    code: string,
    description?: string,
  ): Promise<Record<string, any>> {
    const data = {
      data: { name, code, description },
    };
    return this.session.post('/api/cdp/cdp_devicetype/extend/create', { data });
  }

  async updateDeviceType(
    uuid: string,
    name?: string,
    description?: string,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { data: { uuid } };
    if (name) data.data.name = name;
    if (description) data.data.description = description;
    return this.session.post('/api/cdp/cdp_devicetype/extend/update', { data });
  }

  async deleteDeviceType(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/cdp_devicetype/extend/delete/${uuid}`);
  }

  async getDeviceMeasurePoints(deviceUuid: string): Promise<Record<string, any>[]> {
    const result = await this.session.get('/api/cdp/device/measurePoints', {
      params: { deviceUuid },
    });
    return result?.data ?? [];
  }

  async importDevices(
    projectUuid: string,
    deviceTypeUuid: string,
    file: File | Blob,
  ): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/device/import', {
      data: formData,
      params: { projectUuid, deviceTypeUuid },
      headers: {},
    });
  }

  /** Export devices as Blob. */
  async exportDevices(projectUuid: string, deviceTypeUuid?: string): Promise<Blob> {
    const params: Record<string, any> = { projectUuid };
    if (deviceTypeUuid) params.deviceTypeUuid = deviceTypeUuid;
    return this.session.request('GET', '/api/cdp/device/export', {
      params,
      responseType: 'blob',
    });
  }

  async syncDevices(datasourceUuid: string): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/device/sync', { data: { datasourceUuid } });
  }

  // --- Extended device APIs ---

  async uploadDeviceFile(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/device/upload', { data: formData, params, headers: {} });
  }

  async downloadDeviceTemplate(params?: Record<string, any>): Promise<Blob> {
    return this.session.request('GET', '/api/cdp/device/downloadTemplate', {
      params,
      responseType: 'blob',
    });
  }

  async downloadPdpTemplate(params?: Record<string, any>): Promise<Blob> {
    return this.session.request('GET', '/api/cdp/device/downloadPDPTemplate', {
      params,
      responseType: 'blob',
    });
  }

  async uploadPdp(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/device/uploadPDP', { data: formData, params, headers: {} });
  }

  async getCurrentDayAlarms(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_alarm/currentDay', { params });
  }

  async getSceneLastAlarms(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_alarm/getSceneLastAlarms', { params });
  }

  async uploadModel(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/file/uploadModel', { data: formData, params, headers: {} });
  }

  async uploadVector(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/file/uploadVector', { data: formData, params, headers: {} });
  }

  async getDevicetypeVector(devicetypeUuid: string, params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/file/getDevicetypeVector', {
      params: { devicetypeUuid, ...params },
    });
  }

  async unzipFile(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/file/unzip', { data });
  }

  async deleteDevicetypeModel(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/editor/deleteDevicetypeModel', { data });
  }

  async exportDevicetype(params?: Record<string, any>): Promise<Blob> {
    return this.session.request('GET', '/api/cdp/cdp_devicetype/extend/export', {
      params,
      responseType: 'blob',
    });
  }

  async exportDevicetypeByCatalogs(params?: Record<string, any>): Promise<Blob> {
    return this.session.request('GET', '/api/cdp/cdp_devicetype/extend/exportByCatalogs', {
      params,
      responseType: 'blob',
    });
  }

  async importDevicetype(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/cdp_devicetype/extend/import', { data: formData, params, headers: {} });
  }

  async listDeviceTypesExtend(page = 1, size = 20, params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_devicetype/extend/page', {
      params: { current: page, size, ...params },
    });
  }

  async listDeviceTypesAll(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_devicetype/extend/list', { params });
  }
}
